import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiPlaneLine, RiTeamLine, RiCheckboxCircleLine, RiTimeLine, RiFileTextLine, RiArticleLine, RiCalendarLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiBuilding2Line, RiFileAddLine } from '@remixicon/react';
import { Card, CardHeader, CardContent, CardFooter } from '@components/ui/Card';
import {getOperatorWithLocation} from '@/features/tails/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig } from '../utils';
/**
 * QuoteCard Component
 * Displays a single quote with client info, flight details, and actions
 *
 * @param {Object} props
 * @param {Object} props.quote - Quote data object
 * @param {Function} props.onViewQuote - Callback when "View Quote" is clicked
 */
const QuoteCard = ({ quote, onViewQuote }) => {
  const navigate = useNavigate();

  const handleViewClick = () => {
    if (onViewQuote) {
      onViewQuote(quote);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };


  return (
    <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden bg-white !rounded-3xl">
      {/* Client Info Section */}
      <CardHeader className="p-5 pb-4 !border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative shrink-0">
              {quote.client?.isVerified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <RiCheckboxCircleLine className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>  

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-md truncate">
                {quote.client?.first_name} {quote.client?.last_name}
              </h3>
              <p className="text-xs text-gray-700 truncate flex items-center gap-1">
                <RiBuilding2Line className="w-3 h-3 text-gray-700" />
                <span>{getOperatorWithLocation(quote.bid_items?.[0]?.bid?.bid_options?.[0]?.operator)}</span>
              </p>
            </div>
          </div>

          {(() => {
            const config = getQuoteStatusConfig(quote.status);
            const Icon = config.icon;
            return (
              <span className={`shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {config.label}
              </span>
            );
          })()}
        </div>
      </CardHeader>

      <CardContent className="px-5 pt-0 pb-4 space-y-2">
        {/* Flight Routes */}
        <div className="space-y-2 border-t border-gray-100 pt-2">
          {/* Outbound Flight */}
          {quote.bid_items?.[0]?.bid?.leg_itinerary?.map((leg, i) => (
            <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {leg.departure_airport}</span>
                <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{leg.arrival_airport}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <RiTimeLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm", true)}</span>
                </div>
              </div>
            </div>
          ))}

        </div>

        {/* Additional Info */}
        <div className="text-xs text-gray-500 pt-2 pb-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <RiTeamLine className="w-3.5 h-3.5" />
            <span>{quote.passenger_count || 0} passenger{quote.passenger_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <RiPlaneLine className="w-3.5 h-3.5" />
            <span>{quote.bid_items?.[0]?.bid?.bid_options?.length || 0} tail options</span>
          </div>
        </div>

        {/* View Quote Button */}
        <div className="flex gap-2">
          <button
            onClick={handleViewClick}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors mt-3"
          >
            <RiArticleLine className="w-4 h-4" />
            <span>View Quote</span>
          </button>

          {quote.status=="ACCEPTED" &&
          <button
            onClick={()=> navigate(`/trip-sheet?quoteid=${quote.id}`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 text-white text-sm rounded-full transition-colors mt-3"
          >
            <RiFileAddLine className="w-4 h-4" />
            <span>Build Trip Sheet</span>
          </button>
          }
        </div>
      </CardContent>
    </Card>
  );
};

export default QuoteCard;